import {
  SteelCementRate,
  SteelCementRateFormData,
  GroupedRates,
  RateStatistics,
  ApiResponse,
} from '../../types/steel-cement-rates.types';
import api from './api';

class SteelCementRatesService {
  private readonly endpoint = '/api/steel-cement-rates';

  async getAll(year?: number): Promise<ApiResponse<SteelCementRate[]>> {
    const params = year ? { year } : {};
    const response = await api.get<ApiResponse<SteelCementRate[]>>(this.endpoint, { params });
    return response.data;
  }

  async getGroupedByYear(): Promise<ApiResponse<GroupedRates>> {
    const response = await api.get<ApiResponse<GroupedRates>>(`${this.endpoint}/grouped`);
    return response.data;
  }

  async getYears(): Promise<ApiResponse<number[]>> {
    const response = await api.get<ApiResponse<number[]>>(`${this.endpoint}/years`);
    return response.data;
  }

  async getLatest(limit: number = 12): Promise<ApiResponse<SteelCementRate[]>> {
    const response = await api.get<ApiResponse<SteelCementRate[]>>(
      `${this.endpoint}/latest`,
      { params: { limit } }
    );
    return response.data;
  }

  async getStatistics(): Promise<ApiResponse<RateStatistics>> {
    const response = await api.get<ApiResponse<RateStatistics>>(`${this.endpoint}/statistics`);
    return response.data;
  }

  async getByYearMonth(year: number, month: number): Promise<ApiResponse<SteelCementRate[]>> {
    const response = await api.get<ApiResponse<SteelCementRate[]>>(
      `${this.endpoint}/year/${year}/month/${month}`
    );
    return response.data;
  }

  async getById(id: number): Promise<ApiResponse<SteelCementRate>> {
    const response = await api.get<ApiResponse<SteelCementRate>>(`${this.endpoint}/${id}`);
    return response.data;
  }

  async uploadPdf(file: File): Promise<ApiResponse<{ url: string; filename: string }>> {
    const formData = new FormData();
    formData.append('document', file);
    const response = await api.post<ApiResponse<{ url: string; filename: string }>>(
      `${this.endpoint}/upload-pdf`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  }

  async create(rateData: SteelCementRateFormData): Promise<ApiResponse<SteelCementRate>> {
    const response = await api.post<ApiResponse<SteelCementRate>>(this.endpoint, rateData);
    return response.data;
  }

  async update(
    id: number,
    rateData: Partial<SteelCementRateFormData>
  ): Promise<ApiResponse<SteelCementRate>> {
    const response = await api.put<ApiResponse<SteelCementRate>>(
      `${this.endpoint}/${id}`,
      rateData
    );
    return response.data;
  }

  async delete(id: number): Promise<ApiResponse<void>> {
    const response = await api.delete<ApiResponse<void>>(`${this.endpoint}/${id}`);
    return response.data;
  }

  async toggleStatus(id: number): Promise<ApiResponse<SteelCementRate>> {
    const response = await api.put<ApiResponse<SteelCementRate>>(
      `${this.endpoint}/${id}/toggle-status`
    );
    return response.data;
  }

  async search(searchTerm: string): Promise<ApiResponse<SteelCementRate[]>> {
    const response = await api.get<ApiResponse<SteelCementRate[]>>(this.endpoint, {
      params: { search: searchTerm },
    });
    return response.data;
  }

  async getPaginated(
    page: number,
    limit: number,
    year?: number
  ): Promise<ApiResponse<SteelCementRate[]>> {
    const params: any = { page, limit };
    if (year) params.year = year;
    
    const response = await api.get<ApiResponse<SteelCementRate[]>>(this.endpoint, { params });
    return response.data;
  }
}

export const steelCementRatesService = new SteelCementRatesService();